// ─────────────────────────────────────────────
//  ProfileApplication.jsx
//  Connected to: ProfileLogic.js, Profile.css
//  Used by: Profile.jsx
// ─────────────────────────────────────────────

import React, { useState, useMemo } from "react";
import { matchesCountryString } from "../../../utils/countrySearch";
import { useNavigate } from "react-router-dom";
import { Icons } from "./Profile";
import {
  APPLICATION_STATUS,
  VISIBLE_COUNT,
  formatDate,
  formatCurrency,
  REGION_MAP,
  APP_ALL_REGIONS,
  getAppRegion,
  MEMBER_HELP_CONTACT,
  MEMBER_HELP_TEXT,
  splitSvcFeeGST,
} from "./ProfileLogic.js";
import { TrackingModal, getDecisionConfig } from "./VisaTracking.jsx";
import { generateReceiptHTML } from "../Products/Esim/Edata.js";
import receiptLogo from "../../../assets/PNG/RecieptLogo.png";

// ── Fee tooltip components ──────────────────────────────────────
function ServiceFeeTooltip({ svcFee }) {
  const { base, gst } = splitSvcFeeGST(svcFee);
  return (
    <div className="ProfileFeeInfoWrap" style={{ display: "inline-flex", verticalAlign: "middle", marginLeft: 4 }}>
      <span className="ProfileFeeInfoIcon" style={{ cursor: "pointer", fontSize: 10, width: 14, height: 14, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: "50%", background: "#e5e7eb", color: "#6b7280", fontWeight: 700 }}>i</span>
      <div className="ProfileFeeTooltip ProfileFeeTooltip--left">
        <div className="ProfileFeeTooltipRow">
          <span>Base fee</span>
          <span>₹{base.toLocaleString("en-IN")}</span>
        </div>
        <div className="ProfileFeeTooltipRow">
          <span>GST (18%)</span>
          <span>₹{gst.toLocaleString("en-IN")}</span>
        </div>
      </div>
    </div>
  );
}

function ConvFeeTooltip({ baseFee, gst }) {
  return (
    <div className="ProfileFeeInfoWrap" style={{ display: "inline-flex", verticalAlign: "middle", marginLeft: 4 }}>
      <span className="ProfileFeeInfoIcon" style={{ cursor: "pointer", fontSize: 10, width: 14, height: 14, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: "50%", background: "#e5e7eb", color: "#6b7280", fontWeight: 700 }}>i</span>
      <div className="ProfileFeeTooltip ProfileFeeTooltip--left">
        <div className="ProfileFeeTooltipRow">
          <span>Base conv. fee</span>
          <span>₹{baseFee.toLocaleString("en-IN")}</span>
        </div>
        <div className="ProfileFeeTooltipRow">
          <span>GST (18%)</span>
          <span>₹{gst.toLocaleString("en-IN")}</span>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────
//  RECEIPT DOWNLOAD HELPER
// ────────────────────────────────────────────
const downloadReceipt = async (app, userEmail, userRole) => {
  try {
    const { jsPDF } = await import("jspdf");
    const html2canvas = (await import("html2canvas")).default;
    const toBase64 = async (url) => {
      const resp = await fetch(url);
      const blob = await resp.blob();
      return new Promise((r) => {
        const reader = new FileReader();
        reader.onloadend = () => r(reader.result);
        reader.readAsDataURL(blob);
      });
    };
    let logoSrc = null;
    try { logoSrc = await toBase64(receiptLogo); } catch { logoSrc = null; }

    const count = app.applicants?.length ?? 1;
    const primary = app.applicants?.[0] || {};
    const discAmt = (app.discountAmount || 0) + (app.walletDisc || 0);
    const svcTotal = (app.svcFee || 0) * count;
    const protectTotal = app.protectFee || 0;
    const govTotal = (app.govFee || 0) * count;
    const convTotal = app.convenienceFee || 0;
    const expressTotal = app.expressFee || 0;
    const subtotal = govTotal + svcTotal + protectTotal;
    const total = subtotal + convTotal + expressTotal - discAmt;

    const pi = app.paymentInfo || {};
    let payMethod = "upi";
    if (pi.cardType) payMethod = "card";
    else if (pi.bankName) payMethod = "netbanking";
    else if (pi.upiId) payMethod = "upi";

    const item = {
      label: `Visa Application \u2014 ${app.countryName}`,
      countryName: app.countryName || "",
      price: subtotal,
      data: `${count} Applicant(s)`,
      validity: 1,
      type: "service",
    };

    const html = generateReceiptHTML({
      item, quantity: count, payMethod,
      billingDetails: {
        firstName: [primary.firstName, primary.lastName].filter(Boolean).join(" ") || "",
        lastName: "",
        email: userEmail || app.applicantUserName || "",
        country: app.userCountry || "India",
        whatsapp: "",
        address: "",
        city: "",
        pincode: "",
        accountType: userRole ? `${userRole.charAt(0).toUpperCase() + userRole.slice(1)} Account` : "",
        companyName: app.companyName || "",
      },
      orderId: app.applicationId,
      currency: "INR",
      appliedDiscount: discAmt,
      logoSrc, pdf: true, inr: true,
      upiId: pi.upiId || "",
      bank: pi.bankName || "",
      card: { number: pi.cardNumber || "" },
      simType: "",
      applicants: app.applicants || [],
      authorisedName: app.authorisedName,
      authorisedId: app.authorisedId,
      authorisedPhone: app.authorisedPhone,
      authorisedCountryCode: app.authorisedCountryCode,
      overrides: {
        subtotal,
        tax: 0,
        convFee: convTotal,
        total,
      },
    });

    const container = document.createElement("div");
    container.innerHTML = html;
    container.style.position = "fixed";
    container.style.left = "-9999px";
    container.style.top = "0";
    container.style.width = "210mm";
    container.style.background = "#f5f9ff";
    document.body.appendChild(container);
    const el = container.querySelector(".Receipt-card") || container.firstElementChild;
    const canvas = await html2canvas(el, { scale: 2, useCORS: true, logging: false, backgroundColor: "#f5f9ff" });
    document.body.removeChild(container);

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const imgWidth = 210;
    const pageHeight = 297;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;
    pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
    while (heightLeft > 0) {
      position -= pageHeight;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }
    pdf.save(`TheVisaCo-Receipt-${app.applicationId}.pdf`);
  } catch (err) {
    console.error("PDF generation failed:", err);
  }
};

// ────────────────────────────────────────────
//  MEMBER HELP OVERLAY (same as ProfileOverview)
// ────────────────────────────────────────────
function PhoneIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
    </svg>
  );
}

function MailIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
      <polyline points="22,6 12,13 2,6"/>
    </svg>
  );
}

function WhatsAppIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fill="#fff" d="M4.9,43.3l2.7-9.8C5.9,30.6,5,27.3,5,24C5,13.5,13.5,5,24,5c5.1,0,9.8,2,13.4,5.6	C41,14.2,43,18.9,43,24c0,10.5-8.5,19-19,19c0,0,0,0,0,0h0c-3.2,0-6.3-0.8-9.1-2.3L4.9,43.3z"></path><path fill="#fff" d="M4.9,43.8c-0.1,0-0.3-0.1-0.4-0.1c-0.1-0.1-0.2-0.3-0.1-0.5L7,33.5c-1.6-2.9-2.5-6.2-2.5-9.6	C4.5,13.2,13.3,4.5,24,4.5c5.2,0,10.1,2,13.8,5.7c3.7,3.7,5.7,8.6,5.7,13.8c0,10.7-8.7,19.5-19.5,19.5c-3.2,0-6.3-0.8-9.1-2.3	L5,43.8C5,43.8,4.9,43.8,4.9,43.8z"></path><path fill="#cfd8dc" d="M24,5c5.1,0,9.8,2,13.4,5.6C41,14.2,43,18.9,43,24c0,10.5-8.5,19-19,19h0c-3.2,0-6.3-0.8-9.1-2.3	L4.9,43.3l2.7-9.8C5.9,30.6,5,27.3,5,24C5,13.5,13.5,5,24,5 M24,43L24,43L24,43 M24,43L24,43L24,43 M24,4L24,4C13,4,4,13,4,24	c0,3.4,0.8,6.7,2.5,9.6L3.9,43c-0.1,0.3,0,0.7,0.3,1c0.2,0.2,0.4,0.3,0.7,0.3c0.1,0,0.2,0,0.3,0l9.7-2.5c2.8,1.5,6,2.2,9.2,2.2	c11,0,20-9,20-20c0-5.3-2.1-10.4-5.8-14.1C34.4,6.1,29.4,4,24,4L24,4z"></path><path fill="#40c351" d="M35.2,12.8c-3-3-6.9-4.6-11.2-4.6C15.3,8.2,8.2,15.3,8.2,24c0,3,0.8,5.9,2.4,8.4L11,33l-1.6,5.8	l6-1.6l0.6,0.3c2.4,1.4,5.2,2.2,8,2.2h0c8.7,0,15.8-7.1,15.8-15.8C39.8,19.8,38.2,15.8,35.2,12.8z"></path><path fill="#fff" fill-rule="evenodd" d="M19.3,16c-0.4-0.8-0.7-0.8-1.1-0.8c-0.3,0-0.6,0-0.9,0	s-0.8,0.1-1.3,0.6c-0.4,0.5-1.7,1.6-1.7,4s1.7,4.6,1.9,4.9s3.3,5.3,8.1,7.2c4,1.6,4.8,1.3,5.7,1.2c0.9-0.1,2.8-1.1,3.2-2.3	c0.4-1.1,0.4-2.1,0.3-2.3c-0.1-0.2-0.4-0.3-0.9-0.6s-2.8-1.4-3.2-1.5c-0.4-0.2-0.8-0.2-1.1,0.2c-0.3,0.5-1.2,1.5-1.5,1.9	c-0.3,0.3-0.6,0.4-1,0.1c-0.5-0.2-2-0.7-3.8-2.4c-1.4-1.3-2.4-2.8-2.6-3.3c-0.3-0.5,0-0.7,0.2-1c0.2-0.2,0.5-0.6,0.7-0.8	c0.2-0.3,0.3-0.5,0.5-0.8c2-0.3,0.1-0.6,0-0.8C20.6,19.3,19.7,17,19.3,16z" clip-rule="evenodd"></path>
    </svg>
  );
}

function ArrowRightIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12"/>
      <polyline points="12 5 19 12 12 19"/>
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/>
      <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  );
}

function MemberHelpOverlay({ onClose }) {
  const handlePhone = () => {
    window.location.href = `tel:${MEMBER_HELP_CONTACT.SUPPORT_NUMBER.replace(/\s/g, "")}`;
  };

  const handleEmail = () => {
    window.location.href = `mailto:${MEMBER_HELP_CONTACT.SUPPORT_EMAIL}`;
  };

  const handleWhatsApp = () => {
    const msg = encodeURIComponent(MEMBER_HELP_TEXT.WHATSAPP_PRETEXT);
    window.location.href = `https://wa.me/${MEMBER_HELP_CONTACT.WHATSAPP_NUMBER.replace(/\s/g, "")}?text=${msg}`;
  };

  return (
    <div className="ProfileMemberHelpOverlay" onClick={onClose} data-aos="fade-in" data-aos-delay="50">
      <div className="ProfileMemberHelpModal" onClick={(e) => e.stopPropagation()} data-aos="zoom-in" data-aos-delay="100">
        <div className="ProfileMemberHelpHeader">
          <div className="ProfileMemberHelpHeaderIcon">
            <PhoneIcon />
          </div>
          <div className="ProfileMemberHelpHeaderText">
            <h3 className="ProfileMemberHelpTitle">{MEMBER_HELP_TEXT.TITLE}</h3>
            <p className="ProfileMemberHelpSub">{MEMBER_HELP_TEXT.SUBTITLE}</p>
          </div>
          <button className="ProfileMemberHelpClose" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
        
        <div className="ProfileMemberHelpGrid">
          <div className="ProfileMemberHelpItem" onClick={handlePhone}>
            <div className="ProfileMemberHelpIcon ProfileMemberHelpIcon--phone">
              <PhoneIcon />
            </div>
            <div className="ProfileMemberHelpContent">
              <div className="ProfileMemberHelpLabel">{MEMBER_HELP_TEXT.PHONE_LABEL}</div>
            </div>
            <span className="ProfileMemberHelpAction">
              <ArrowRightIcon />
            </span>
          </div>

          <div className="ProfileMemberHelpItem" onClick={handleEmail}>
            <div className="ProfileMemberHelpIcon ProfileMemberHelpIcon--mail">
              <MailIcon />
            </div>
            <div className="ProfileMemberHelpContent">
              <div className="ProfileMemberHelpLabel">{MEMBER_HELP_TEXT.EMAIL_LABEL}</div>
            </div>
            <span className="ProfileMemberHelpAction">
              <ArrowRightIcon />
            </span>
          </div>

          <div className="ProfileMemberHelpItem ProfileMemberHelpItem--whatsapp" onClick={handleWhatsApp}>
            <div className="ProfileMemberHelpIcon ProfileMemberHelpIcon--whatsapp">
              <WhatsAppIcon />
            </div>
            <div className="ProfileMemberHelpContent">
              <div className="ProfileMemberHelpLabel">{MEMBER_HELP_TEXT.WHATSAPP_LABEL}</div>
            </div>
            <span className="ProfileMemberHelpAction">
              <ArrowRightIcon />
            </span>
          </div>
        </div>

        <div className="ProfileMemberHelpNote">
          {MEMBER_HELP_TEXT.HELP_NOTE}
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────
//  APPLICATION CARD
// ────────────────────────────────────────────
export function ApplicationCard({ app, applications = [], onPay, onDelete, onSetDecision, hasDraft, onHelpClick, profileComplete, onGoToDocuments, userEmail, userRole }) {
  const [trackingOpen, setTrackingOpen] = useState(false);
  const isPending      = app.status === APPLICATION_STATUS.PENDING;
  const isCompleted    = app.status === APPLICATION_STATUS.COMPLETED;
  const isProcessing   = isCompleted && !app.decisionStatus;
  const hasDecision    = isCompleted && !!app.decisionStatus;
  const decisionCfg    = isCompleted ? getDecisionConfig(app) : null;

  const countryPrefix = (app.countryName || "").slice(0, 3).toUpperCase();
  const appId = app.applicationId || app.id || app._id;
  const appIdx = applications.findIndex((a) => (a.applicationId || a.id || a._id) === appId);
  const seqNum = String(appIdx + 1).padStart(3, "0");
  const displayId = `${countryPrefix}${seqNum}`;

  const govFee    = app.govFee || 0;
  const svcFee    = app.svcFee || 0;
  const protect   = app.protectFee ?? 0;
  const convFee   = app.convenienceFee || 0;
  const express   = app.expressFee || 0;
  const discount  = (app.discountAmount || 0) + (app.walletDisc || 0);
  const total     = app.totalFee ?? (govFee + svcFee + protect + convFee + express);

  const fmtDate = (d) => {
    if (!d) return "—";
    const date = new Date(d);
    return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  };

  return (
    <div className="ProfileAppCard" data-aos="fade-up">
      <div className="ProfileAppCardHeader">
        <div className="ProfileAppCardInfo">
          <div className="ProfileAppCardCountry">{app.countryName || app.destination || "—"}</div>
          <div className="ProfileAppCardType">
            {app.visaTypeLabel || app.type || "Visa Application"}
            {app.expressVisa && (
              <span className="ProfileExpressBadge">&#9889; Express</span>
            )}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2, alignSelf: "flex-start", marginTop: 2, marginLeft: "auto", marginRight: 4 }}>
          <button
            className="ProfileAppCardDeleteBtn"
            onClick={() => onDelete(app.applicationId || app.id || app._id)}
            title="Remove application"
          >
            ✕
          </button>
          <div className="ProfileAppCardUpdatedat">Updates {fmtDate(app.updatedAt || app.appliedAt)}</div>
        </div>
      </div>

      <div className="ProfileAppCardBody">
        <div className="ProfileAppCardMetaRow">
          <div className="ProfileAppCardMetaItem">
            <div className="ProfileAppCardMetaLabel">Application ID</div>
            <div className="ProfileAppCardMetaValue ProfileAppCardId">{displayId}</div>
          </div>

          {govFee > 0 && (
            <div className="ProfileAppCardMetaItem">
              <div className="ProfileAppCardMetaLabel">Govt Fee</div>
              <div className="ProfileAppCardMetaValue">{formatCurrency(govFee)}</div>
            </div>
          )}
          {svcFee > 0 && (
            <div className="ProfileAppCardMetaItem">
              <div className="ProfileAppCardMetaLabel">Service Fee</div>
              <div className="ProfileAppCardMetaValue">
                {formatCurrency(svcFee)}
                <ServiceFeeTooltip svcFee={svcFee} />
              </div>
            </div>
          )}
          {protect > 0 && (
            <div className="ProfileAppCardMetaItem">
              <div className="ProfileAppCardMetaLabel">Visa Protect</div>
              <div className="ProfileAppCardMetaValue">{formatCurrency(protect)}</div>
            </div>
          )}
          {convFee > 0 && (
            <div className="ProfileAppCardMetaItem">
              <div className="ProfileAppCardMetaLabel">Convenience Fee</div>
              <div className="ProfileAppCardMetaValue">
                {formatCurrency(convFee)}
                <ConvFeeTooltip baseFee={app.convenienceBaseFee || 0} gst={app.convenienceGst || 0} />
              </div>
            </div>
          )}
          {express > 0 && (
            <div className="ProfileAppCardMetaItem">
              <div className="ProfileAppCardMetaLabel">⚡ Express Fee</div>
              <div className="ProfileAppCardMetaValue ProfileAppCardMetaValue--express">{formatCurrency(express)}</div>
            </div>
          )}
          {total > 0 && (
            <div className="ProfileAppCardMetaItem">
              <div className="ProfileAppCardMetaLabel">Total Fees</div>
              <div className="ProfileAppCardMetaValue ProfileAppCardMetaValue--total">{formatCurrency(total)}</div>
            </div>
          )}
          {discount > 0 && (
            <div className="ProfileAppCardMetaItem">
              <div className="ProfileAppCardMetaLabel">Discount</div>
              <div className="ProfileAppCardMetaValue ProfileAppCardMetaValue--discount">−{formatCurrency(discount)}</div>
            </div>
          )}
          {hasDecision && (() => {
            const cfg = getDecisionConfig(app);
            if (!cfg) return null;
            return (
              <div className="ProfileAppCardMetaItem ProfileAppCardMetaItem--full">
                <div className="ProfileAppCardMetaLabel">Decision</div>
                <div className="ProfileAppCardMetaValue">
                  <span className={`ProfileDecisionValue ProfileDecisionValue--${app.decisionStatus}`}>{
                    app.decisionStatus === "approved" ? "✅ Approved" :
                    app.decisionStatus === "rejected" ? "❌ Rejected" : "⏸ On Hold"
                  }</span>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      <div className="ProfileAppCardFooter">
        <button className="ProfileBtnTrack" onClick={() => setTrackingOpen(true)}>
          📍 Track
        </button>
        {isProcessing && (
          <button className="ProfileReceiptBtn" onClick={() => downloadReceipt(app, userEmail, userRole)}>
            Receipt
          </button>
        )}
        {(isProcessing || hasDecision) && onHelpClick && (
          <button className="ProfileMemberIconBtn" onClick={() => onHelpClick()} title="Need Help? Contact Member Support">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
          </button>
        )}
        {isPending && !app.paid && (
          <button
            className="ProfileBtnPay ProfileBtnPay--sm"
            style={{ marginLeft: "auto", opacity: profileComplete ? 1 : 0.5 }}
            onClick={() => {
              if (!profileComplete) { alert("Please upload your documents before proceeding to payment."); onGoToDocuments?.(); return; }
              onPay(app);
            }}
          >
            {hasDraft ? <>Complete</> : <>Pay</>}
          </button>
        )}
      </div>

      {trackingOpen && <TrackingModal app={app} onClose={() => setTrackingOpen(false)} />}
    </div>
  );
}

// ────────────────────────────────────────────
//  APPLICATIONS TAB
// ────────────────────────────────────────────
export default function ApplicationsTab({
  applications,
  pendingApplications,
  completedApplications,
  processingApplications,
  decisionApplications,
  onPay,
  onDelete,
  onSetDecision,
  draftMap,
  profileComplete,
  onGoToDocuments,
  initialFilter,
  userEmail,
  userRole,
}) {
  const navigate = useNavigate();

  const [filter,           setFilter]           = useState(initialFilter || "pending");
  const [searchQuery,      setSearchQuery]     = useState("");
  const [showAll,          setShowAll]          = useState(false);
  const [region,           setRegion]           = useState("All");
  const [memberHelpOpen,   setMemberHelpOpen]   = useState(false);

  const procList = processingApplications ?? completedApplications.filter((a) => !a.decisionStatus);
  const decList  = decisionApplications   ?? completedApplications.filter((a) => !!a.decisionStatus);

  const source = filter === "completed" ? procList
               : filter === "decision"  ? decList
               : pendingApplications;

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return source.filter((app) => {
      if (region !== "All") {
        if (getAppRegion(app.countryName) !== region) return false;
      }
      const countryMatch = matchesCountryString(app.countryName || "", q);
      const visaMatch    = app.visaTypeLabel?.toLowerCase().includes(q);
      const idMatch      = app.applicationId?.toLowerCase().includes(q);
      const nameMatch    = (app.applicants || []).some((a) =>
        [a.firstName, a.lastName].filter(Boolean).join(" ").toLowerCase().includes(q)
      );
      return countryMatch || visaMatch || idMatch || nameMatch;
    });
  }, [source, searchQuery, region, filter]);

  const displayed = showAll ? filtered : filtered.slice(0, VISIBLE_COUNT);
  const hasMore   = filtered.length > VISIBLE_COUNT;

  function handleFilterChange(val) {
    setFilter(val);
    setShowAll(false);
    setSearchQuery("");
    setRegion("All");
  }

  return (
    <div className="ProfileTabSection">
      {/* Global Member Help Overlay */}
      {memberHelpOpen && <MemberHelpOverlay onClose={() => setMemberHelpOpen(false)} />}
      
      {/* ── Filter bar ── */}
      <div className="ProfileAppFilterBar" data-aos="fade-up" data-aos-delay="50">
        <button
          className={`ProfileAppFilterBtn ${filter === "pending" ? "active" : ""}`}
          onClick={() => handleFilterChange("pending")}
        >
          Pending{" "}
          <span className="ProfileAppFilterCount">{pendingApplications.length}</span>
        </button>
        <button
          className={`ProfileAppFilterBtn ${filter === "completed" ? "active" : ""}`}
          onClick={() => handleFilterChange("completed")}
        >
          Processing{" "}
          <span className="ProfileAppFilterCount">{procList.length}</span>
        </button>
        <button
          className={`ProfileAppFilterBtn ${filter === "decision" ? "active" : ""} ${filter !== "decision" && decList.length > 0 ? "ProfileAppFilterBtn--has-decision" : ""}`}
          onClick={() => handleFilterChange("decision")}
        >
          Decision{" "}
          <span className={`ProfileAppFilterCount${decList.length > 0 ? " ProfileAppFilterCount--has-decision" : ""}`}>{decList.length}</span>
        </button>
      </div>

      {/* ── Search bar ── */}
      <div className="ProfileAppSearchRow" data-aos="fade-up" data-aos-delay="100">
        <div className="ProfileCommSearchInput">
          <Icons.Search />
          <input
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setShowAll(false); }}
            placeholder="Search by country, visa type, ID or applicant name…"
          />
          {searchQuery && (
            <button
              className="ProfileSearchClearBtn"
              onClick={() => setSearchQuery("")}
              title="Clear"
            >
              <Icons.Close />
            </button>
          )}
        </div>
      </div>

      {/* ── Region filter pills ── */}
      <div className="ProfileRegionPills">
          {APP_ALL_REGIONS.map((r, i) => (
            <button
              key={r}
              className={`ProfileRegionPill ${region === r ? "active" : ""}`}
              onClick={() => { setRegion(r); setShowAll(false); }}
            >
              {r}
            </button>
          ))}
        </div>

      {/* ── Empty state ── */}
      {filtered.length === 0 ? (
        <>
          <div className="ProfileAppEmpty" data-aos="fade-up" data-aos-delay="100">
            {/* <div className="ProfileAppEmptyIcon">
              {filter === "completed" ? <Icons.Check /> : <Icons.Clock />}
            </div> */}
            <div className="ProfileAppEmptyTitle">
              {searchQuery
                ? "No applications match your search"
                : filter === "completed"
                ? "No applications in processing"
                : filter === "decision"
                ? "No decisions yet"
                : "No pending applications"}
            </div>
            <div className="ProfileAppEmptySub">
              {searchQuery
                ? "Try a different search term."
                : filter === "pending"
                ? "Start an application from any country page."
                : filter === "decision"
                ? "Decisions will appear here once the embassy responds."
                : "Paid applications appear here while being processed."}
            </div>
          </div>

          {/* Browse banner even when empty */}
          <div className="ProfileBrowseBanner" data-aos="fade-up" data-aos-delay="150">
            <div className="ProfileBrowseBannerLeft">
              <div className="ProfileBrowseBannerIcon">🌍</div>
              <div>
                <div className="ProfileBrowseBannerTitle">Explore more destinations</div>
                <div className="ProfileBrowseBannerSub">
                  Search visa requirements for 190+ countries
                </div>
              </div>
            </div>
            <button className="ProfileBtnBrowse" onClick={() => navigate("/")}>
              Explore 
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="ProfileAppList">
            {displayed.map((app, i) => (
              <ApplicationCard
                key={app.applicationId}
                app={app}
                applications={applications}
                onPay={onPay}
                onDelete={onDelete}
                onSetDecision={onSetDecision}
                hasDraft={!!(draftMap?.[app.applicationId])}
                onHelpClick={() => setMemberHelpOpen(true)}
                profileComplete={profileComplete}
                onGoToDocuments={onGoToDocuments}
                userEmail={userEmail}
                userRole={userRole}
              />
            ))}
          </div>

          {/* View More / View Less */}
          {hasMore && (
            <button
              className="ProfileBtn ProfileBtnOutline ProfileBtnSm ProfileAppViewMoreBtn"
              onClick={() => setShowAll(!showAll)}
              data-aos="fade-up"
              data-aos-delay="100"
            >
              {showAll
                ? "Show Less"
                : `View All ${filtered.length} Applications`}
            </button>
          )}

{/* ── Browse Destinations Banner ── */}
          <div className="ProfileBrowseBanner" data-aos="fade-up" data-aos-delay="150">
            <div className="ProfileBrowseBannerLeft">
              <div className="ProfileBrowseBannerIcon">🌍</div>
              <div>
                <div className="ProfileBrowseBannerTitle">Explore more destinations</div>
                <div className="ProfileBrowseBannerSub">
                  Search visa requirements for 190+ countries
                </div>
              </div>
            </div>
            <button className="ProfileBtnBrowse" onClick={() => navigate("/")}>
              Explore 
            </button>
          </div>
        </>
      )}

      {/* Global Member Help Overlay */}
      {memberHelpOpen && <MemberHelpOverlay onClose={() => setMemberHelpOpen(false)} />}
    </div>
  );
}